import { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { absolutizeReplicatedImageSrcs, REPLICATED_IMAGE_PATH, type RpLayout } from '@mlm/shared';
import { RpPageRenderer, type RpPackageCard, type RpRenderCtx } from '@mlm/ui';
import { API_BASE_URL } from '../lib/api';

/**
 * Device-accurate preview target for the admin page builder.
 *
 * The admin embeds this route in an iframe and posts the draft into it. Why an
 * iframe rather than rendering the draft inside the admin: Tailwind's `sm:`/
 * `md:`/`lg:` are VIEWPORT media queries, so a 390px-wide div inside a 1440px
 * admin window still renders desktop styles. An in-admin "mobile" toggle would
 * therefore be a lie — and these links are opened overwhelmingly on phones. An
 * iframe has its own viewport, so narrowing it genuinely triggers the mobile
 * breakpoints. It also inherits the tenant's real theme and fonts.
 *
 * Its own path namespace (`/__rp-preview`), not under `/r/`, so it can never
 * collide with a referral code.
 *
 * SECURITY: this route renders HTML handed to it over postMessage.
 *  - the sender's origin is checked against the expected admin origin,
 *  - the payload is shape-checked before use,
 *  - rich text goes through the SAME DOMPurify pass as the live page — authoring
 *    is never a trusted-HTML mode,
 *  - nothing renders until a valid message arrives, so opening the URL directly
 *    shows an empty shell.
 */

type PreviewMessage = {
  __rp: 'draft';
  layout: RpLayout;
  ctaLabel?: string;
  ctaAuto?: boolean;
  packages?: RpPackageCard[];
  selectedId?: string | null;
};

/**
 * The origin the admin app is served from.
 *
 * Production serves admin at `admin.<domain>` and the member app at the apex and
 * `member.<domain>` (see Caddyfile.prod), so the admin origin is derivable from
 * our own host. `VITE_ADMIN_URL` overrides it for local dev, where the two are
 * different ports on localhost.
 */
function expectedAdminOrigin(): string {
  const override = import.meta.env.VITE_ADMIN_URL;
  if (override) return String(override).replace(/\/$/, '');
  const { protocol, hostname, port } = window.location;
  const host = hostname.replace(/^member\./, '');
  return `${protocol}//admin.${host}${port ? `:${port}` : ''}`;
}

function isPreviewMessage(v: unknown): v is PreviewMessage {
  if (!v || typeof v !== 'object') return false;
  const m = v as Record<string, unknown>;
  if (m.__rp !== 'draft') return false;
  const layout = m.layout as { sections?: unknown } | undefined;
  return !!layout && Array.isArray(layout.sections);
}

export function ReplicatedPreviewPage() {
  const [draft, setDraft] = useState<PreviewMessage | null>(null);

  useEffect(() => {
    const adminOrigin = expectedAdminOrigin();

    function onMessage(e: MessageEvent) {
      // Only the admin app may drive this page.
      if (e.origin !== adminOrigin) return;
      if (!isPreviewMessage(e.data)) return;
      setDraft(e.data);
    }
    window.addEventListener('message', onMessage);

    // Announce readiness so the admin knows the frame is listening and stops
    // waiting on its fallback timer.
    window.parent?.postMessage({ __rp: 'ready' }, adminOrigin);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Keep this page out of search results — it is a tool, not content.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const ctx = useMemo<RpRenderCtx>(
    () => ({
      link: { ref: 'SAMPLE' },
      imageUrl: (key) => `${API_BASE_URL}${REPLICATED_IMAGE_PATH}?key=${encodeURIComponent(key)}`,
      legacyImageUrl: (blockId) => `${API_BASE_URL}/replicated-page/blocks/${blockId}/image`,
      renderHtml: (html) =>
        DOMPurify.sanitize(absolutizeReplicatedImageSrcs(html, API_BASE_URL), {
          ADD_TAGS: ['iframe'],
          ADD_ATTR: ['allowfullscreen', 'frameborder', 'target'],
        }),
      mode: 'preview',
      selectedSectionId: draft?.selectedId ?? null,
      packages: draft?.packages ?? [],
      onSelectSection: (id) => {
        // Clicking a section in the preview selects it in the admin inspector.
        window.parent?.postMessage({ __rp: 'select', id }, expectedAdminOrigin());
      },
    }),
    [draft?.selectedId, draft?.packages],
  );

  // Scroll the selected section into view when the admin picks one in the list.
  useEffect(() => {
    if (!draft?.selectedId) return;
    const el = document.querySelector(`[data-rp-section-id="${CSS.escape(draft.selectedId)}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [draft?.selectedId]);

  if (!draft) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Preview
      </div>
    );
  }

  return (
    <RpPageRenderer
      layout={draft.layout}
      ctx={ctx}
      footer={
        draft.ctaAuto !== false ? (
          <div className="pt-2 text-center">
            <span className="inline-block rounded-md bg-primary px-8 py-3 text-base font-semibold text-primary-foreground">
              {draft.ctaLabel || 'Sign up'}
            </span>
          </div>
        ) : null
      }
    />
  );
}
